import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { cartApi } from '../api/endpoints';
import { useAuth } from './AuthContext';

const CartContext = createContext(null);
const CART_KEY = 'nv_cart';

function authRequiredError() {
  const err = new Error('Please log in to add items to your bag');
  err.code = 'AUTH_REQUIRED';
  return err;
}

export function CartProvider({ children }) {
  const { isLoggedIn } = useAuth();
  const [items, setItems] = useState([]);
  const [peacockBurst, setPeacockBurst] = useState(false);

  const normalize = (cart) => {
    if (!cart) return [];
    if (Array.isArray(cart)) return cart;
    return (cart.items || []).map((i) => ({
      productId: i.product?._id || i.productId,
      product: i.product,
      size: i.size || '',
      quantity: i.quantity || 1,
    }));
  };

  const refresh = useCallback(async () => {
    if (!isLoggedIn) {
      setItems([]);
      return;
    }
    const res = await cartApi.get();
    setItems(normalize(res.data.data));
  }, [isLoggedIn]);

  useEffect(() => {
    refresh().catch(() => setItems([]));
  }, [refresh]);

  const triggerPeacock = () => {
    setPeacockBurst(true);
    setTimeout(() => setPeacockBurst(false), 2100);
  };

  const addItem = async (product, size = '', quantity = 1) => {
    if (!isLoggedIn) throw authRequiredError();
    const productId = product._id || product;
    const res = await cartApi.add({ productId, size, quantity });
    setItems(normalize(res.data.data));
    // Clear any leftover guest cart from older sessions
    localStorage.removeItem(CART_KEY);
    triggerPeacock();
  };

  const updateItem = async (productId, size, quantity) => {
    if (!isLoggedIn) throw authRequiredError();
    const res = await cartApi.update({ productId, size, quantity });
    setItems(normalize(res.data.data));
  };

  const removeItem = async (productId, size = '') => {
    if (!isLoggedIn) throw authRequiredError();
    const res = await cartApi.remove({ productId, size });
    setItems(normalize(res.data.data));
  };

  const count = useMemo(
    () => items.reduce((sum, i) => sum + (i.quantity || 1), 0),
    [items]
  );

  return (
    <CartContext.Provider
      value={{ items, count, addItem, updateItem, removeItem, refresh, peacockBurst }}
    >
      {children}
    </CartContext.Provider>
  );
}

export function useCart() {
  const ctx = useContext(CartContext);
  if (!ctx) throw new Error('useCart must be used within CartProvider');
  return ctx;
}
